/**
 * Apollo Client singleton configuration for GraphQL data fetching.
 * Includes auth link (JWT token injection), error link (error handling and logout on 401),
 * and type policies for normalized caching.
 */
import { ApolloClient, InMemoryCache, TypePolicies, createHttpLink, from } from '@apollo/client';

import authServices from '@/auth/main/DependencyInjection';
import { createApolloAuthLink } from '@/auth/infrastructure/ApolloAuthLink';
import { createApolloErrorLink } from '@/auth/infrastructure/ApolloErrorLink';
import introspectionResult from '../generated/introspection.json';

// Generated by codegen

// Extract possible types from introspection result
const possibleTypes = introspectionResult.__schema.types.reduce(
  (acc, type) => {
    if (type.possibleTypes) {
      acc[type.name] = type.possibleTypes.map((subtype: { name: string }) => subtype.name);
    }
    return acc;
  },
  {} as Record<string, string[]>
);

const typePolicies: TypePolicies = {
  // Disable normalization for Equipment since the same equipment can have different
  // plannedAllocation values for different jobs. Normalizing by ID causes all jobs
  // with the same equipment to share the same plannedAllocation values.
  Dtos_V2_Equipment_EquipmentDto: { keyFields: false },
  // Crew items inside some queries (e.g., listJobs) don't include 'id'.
  // Disable normalization for this type to avoid Apollo invariant errors.
  Dtos_V2_CrewDto: { keyFields: false },
  Dtos_V1_VendorDto: { keyFields: false },
  Dtos_V2_ExtraSystemDto: { keyFields: ['id'] },

  // Clarify how queries with args are keyed in the cache
  Query: {
    fields: {
      // Each set of args represents a distinct result set in cache.
      // We don't merge pages because the UI uses server pagination.
      availableEquipments: {
        keyArgs: ['where', 'order', 'availableEquipmentsInput', 'skip', 'take']
      },
      availableCrewMembers: {
        keyArgs: ['where', 'order', 'availableCrewMembersInput', 'skip', 'take']
      },
      extraSystems: {
        keyArgs: ['where', 'order', 'skip', 'take']
      },
      raptorJobs: {
        keyArgs: ['where', 'order', 'startDate', 'endDate', 'skip', 'take']
      }
    }
  }
};

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies,
  // Enable result caching for better performance
  resultCaching: true
});

// Monitor and limit cache size to prevent memory leaks
// Apollo Client doesn't have built-in size limits, so we manually evict old data
// Store interval ID for cleanup if needed (though this runs for app lifetime)
let cacheEvictionIntervalId: ReturnType<typeof setInterval> | null = null;

if (typeof window !== 'undefined') {
  // Periodically check and evict cache if it gets too large
  cacheEvictionIntervalId = setInterval(
    () => {
      try {
        const cacheSize = cache.extract();
        const cacheKeys = Object.keys(cacheSize);

        // If cache has more than 1000 entries, evict oldest queries
        // This is a conservative limit to prevent memory issues
        if (cacheKeys.length > 1000) {
          // Clear ROOT_QUERY entries that are likely stale
          // This is a simple eviction strategy - in production, you might want more sophisticated logic
          const rootQuery = cache.extract(true);
          if (rootQuery.ROOT_QUERY) {
            const rootQueryKeys = Object.keys(rootQuery.ROOT_QUERY);
            // Evict oldest 20% of queries
            const keysToEvict = rootQueryKeys.slice(0, Math.floor(rootQueryKeys.length * 0.2));
            keysToEvict.forEach((key) => {
              cache.evict({ id: 'ROOT_QUERY', fieldName: key });
            });
            cache.gc();
          }
        }
      } catch (error) {
        // Silently fail cache eviction to avoid breaking the app
        console.warn('Cache eviction failed:', error);
      }
    },
    5 * 60 * 1000
  ); // Check every 5 minutes
}

// Export cleanup function for testing or if needed
export const clearCacheEvictionInterval = () => {
  if (cacheEvictionIntervalId) {
    clearInterval(cacheEvictionIntervalId);
    cacheEvictionIntervalId = null;
  }
};

const graphqlEndpoint = import.meta.env?.VITE_GRAPHQL_URL || '';
const queryVersion = '/v2';

// HTTP link for connecting to the GraphQL server
const httpLink = createHttpLink({
  uri: `${graphqlEndpoint}${queryVersion ?? ''}`
});

// Get token service from auth module
const { tokenService } = authServices;

// Create auth link using the token service
const authLink = createApolloAuthLink(tokenService);

// Create error link for handling GraphQL errors, including auth errors
const errorLink = createApolloErrorLink();

// Create Apollo Client with Auth Middleware and Error Handling
// Order matters: errorLink should be before httpLink to catch any GraphQL errors
// The flow is: errorLink -> authLink -> httpLink
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
  devtools: { enabled: typeof window !== 'undefined' && process.env.NODE_ENV === 'development' },
  defaultOptions: { query: { fetchPolicy: 'cache-first' } }
});

export default client;
